import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import IconosGenerales from "../iconos/IconosGenerales";
import { visuallyHidden } from '@mui/utils';
import { styled } from '@mui/material/styles';
import PaginacionTabla from '../eventos/filtros/SelectPaginaTabla';
import SelectNumeroCursos from '../eventos/filtros/SelectNumeroElementos';
import SelectDinamicoLista from '../eventos/filtros/SelectDinamicoLista';
import Buscador from '../eventos/filtros/BuscadorDinamico';
import funcionOrdenarColumnas from '../eventos/filtros/OrdenarColumnas';
import { useNavigate, useLocation } from 'react-router-dom';
import CustomTooltip from '../eventos/avisos/Tooltip';
import BotonesAccion from "../eventos/navegacion_y_accion/BotonesAccion";
import { CircularProgress, Autocomplete, TextField } from "@mui/material";
import ProtectedView from "../../providers/ProtectedView";
import { BotonExportarExcel } from "../../componentes/incorporaciones/BotonExportarExcel";
import { Button, Col } from 'react-bootstrap';
import LogoAnyDesk from './../../img/anydesk_logo.png';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import ImagenDefault from './../../img/imgDefault.png';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GorraCumpleanos from './../../img/gorra_cumpleaños.png';
import Chip from '@mui/material/Chip';
import { UsuarioContext } from "../../providers/personaContext";
import { puedeAbrirFichas } from "../../providers/rolesFicha";
import { CeldaCorreo } from "../../componentes/correos/GruposDelBuscador";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    textAlign: 'center  !important',
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: 'rgba(0, 60, 102, 0.8)',
        color: theme.palette.common.white,
        padding: '0px',
    },
}));

/* ── EL COLOR DE LA FILA LO DECIDE LA SITUACION, NO `activo` ──────────────────────────────────
 *
 * Hasta Fase C era `row.activo === false` y un solo rojo. Pero SIN ACCESO no es una situacion:
 * son TRES, y el rojo es el de una sola. Salian identicas:
 *
 *   Baja         se fue                        ← el rojo es suyo
 *   En proceso   todavia no ha empezado        ← pintado como si se hubiera ido
 *   Suspendido   esta, con el acceso retirado  ← igual
 *
 * Y habia una segunda cosa: `activo` viaja en la lista blanca del listado A TODOS LOS ROLES,
 * mientras que la INSIGNIA el servidor solo se la manda a quien le toca verla. Pintar la fila
 * desde `activo` contaba con un color lo que el servidor se estaba callando a proposito.
 *
 * Por eso la clave es `insignia.clave` —la palabra del servidor— y no el id del estado:
 *
 *   · no se duplica aqui la regla de que estado da acceso, que ya vive en `presenciaHoy.js`
 *   · un estado nuevo que el servidor no clasifique cae en el hueco, no en un color equivocado
 *   · y SIN insignia no se pinta nada, asi que el color nunca dice mas que el chip
 *
 * «Baja medica» no se pinta: tiene acceso, y se queda como estaba.
 */
const FONDO_FILA = {
    baja:       '#ffcdd2',   // rojo, el de siempre. Es el terminal
    en_proceso: '#fde68a',   // ambar del sistema: todavia no, no «ya no»
    suspendido: '#e2e8f0',   // gris: esta, pero sin acceso ahora
};
const fondoDe = (row) => (row && row.insignia && FONDO_FILA[row.insignia.clave]) || null;

const StyledTableRow = styled(TableRow, {
    // `fondo` es para el estilo, no un atributo del DOM: sin esto React avisa por consola.
    shouldForwardProp: (prop) => prop !== 'fondo',
})(({ theme, fondo }) => ({
    textAlign: 'center  !important',
    '&:nth-of-type(odd)': {
        backgroundColor: fondo || theme.palette.action.hover,
    },
    '&:nth-of-type(even)': {
        backgroundColor: fondo || 'transparent',
    },
    '&:hover': {
        backgroundColor: fondo
            ? `${fondo} !important`
            : `${theme.palette.action.hover} !important`,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const WhiteTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
    textAlign: 'center  !important',
    color: `${theme.palette.common.white} !important`,
    '&.Mui-active .MuiTableSortLabel-icon': {
        color: `${theme.palette.common.white} !important`,
    },
}));

const SwitchActivo = styled(Switch)(({ theme, indeterminate }) => ({
    '& .MuiSwitch-thumb': {
        ...(indeterminate && {
            transform: 'translateX(11px)', // Posición intermedia gipsy
            backgroundColor: theme.palette.warning.main,
        }),
    },
    '& .MuiSwitch-track': {
        ...(indeterminate && {
            backgroundColor: theme.palette.warning.light,
            opacity: 0.5,
        }),
    },
    // Estado activo (checked = true) - Verde
    '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
        backgroundColor: theme.palette.success.main,
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: theme.palette.success.light
    },
    // Estado inactivo (checked = false) - Rojo
    '&:not(.Mui-checked) .MuiSwitch-thumb': {
        backgroundColor: indeterminate ? theme.palette.warning.main : theme.palette.error.main,
    },
    '&:not(.Mui-checked) .MuiSwitch-track': {
        backgroundColor: indeterminate ? theme.palette.warning.light : theme.palette.error.light,
        opacity: indeterminate ? 0.5 : 0.3,
    },
}));

function IndeterminateSwitch({ checked, indeterminate, onChange, ...props }) {
    const [isDragging, setIsDragging] = React.useState(false);
    const [dragStartX, setDragStartX] = React.useState(0);
    const switchRef = React.useRef(null);

    const handleMouseDown = (event) => {
        if (indeterminate) {
            setIsDragging(true);
            setDragStartX(event.clientX);
            event.preventDefault();
        }
    };

    const handleMouseMove = React.useCallback((event) => {
        if (isDragging && indeterminate) {
            const deltaX = event.clientX - dragStartX;
            const threshold = 10;
            
            if (Math.abs(deltaX) > threshold) {
                const newChecked = deltaX > 0; // Positivo = derecha (encender), Negativo = izquierda (apagar)
                onChange({ target: { checked: newChecked } });
                setIsDragging(false);
            }
        }
    }, [isDragging, dragStartX, onChange, indeterminate]);

    const handleMouseUp = React.useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
        }
    }, [isDragging]);

    React.useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const handleClick = (event) => {
        if (!isDragging) {
            if (indeterminate) {
                // En estado intermedio, un clic simple lo enciende
                onChange({ target: { checked: true } });
            } else {
                onChange(event);
            }
        }
    };

    return (
        <SwitchActivo
            ref={switchRef}
            checked={indeterminate ? false : checked}
            indeterminate={indeterminate}
            onChange={handleClick}
            onMouseDown={handleMouseDown}
            {...props}
        />
    );
}

function stableSort(array, comparator) {
    const arrayCopy = [...array];
    arrayCopy.sort(comparator);
    return arrayCopy;
}

function EnhancedTableHead(props) {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <StyledTableRow>
                <StyledTableCell padding="checkbox">
                    <ProtectedView allowedRoles={["administrador", "super administrador"]}>
                        <Checkbox
                            style={{ color: 'white' }}
                            indeterminate={numSelected > 0 && numSelected < rowCount}
                            checked={rowCount > 0 && numSelected === rowCount}
                            onChange={onSelectAllClick}
                            inputProps={{
                                'aria-label': 'select all desserts',
                            }}
                        />
                    </ProtectedView>
                </StyledTableCell>
                {props.header.map((headCell) => (
                    <StyledTableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={headCell.id === 'telefono_empresa' ? { minWidth: 130, whiteSpace: 'nowrap' } : undefined}
                    >
                        <WhiteTableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </WhiteTableSortLabel>
                    </StyledTableCell>
                ))}
            </StyledTableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

/* ── EL PUNTO DE SITUACIÓN, JUNTO AL NOMBRE ────────────────────────────────────────────────
 *
 * **No sólo color.** El color solo no lo lee quien no distingue verde de gris, ni un lector de
 * pantalla. Así que el punto lleva:
 *
 *   · `title`      → el texto al pasar el ratón
 *   · `aria-label` → lo que anuncia un lector de pantalla, con `role="img"`
 *
 * Un punto sin texto obliga a aprenderse un código de colores que nadie ha publicado.
 *
 * Y si no hay insignia —porque a quien mira no le toca verla— **no se pinta nada**, ni un punto
 * gris: no se dice, que es distinto de decir «Inactivo», que ya sería información. Se reserva el
 * hueco para que los nombres no bailen entre filas.
 *
 * La insignia llega HECHA del servidor, ya filtrada por rol. Aquí sólo se traduce a color: si la
 * regla viviera también aquí, el día que entre un estado nuevo se actualizaría uno de los dos.
 */
const COLOR_PUNTO = {
    verde: '#16a34a',
    azul:  '#2563eb',
    ambar: '#d97706',
    gris:  '#94a3b8',
};

/* El mismo mapa que la ficha. El tono lo decide el servidor —verde, azul, ámbar, gris—; aquí
 * sólo se traduce al color de MUI, para que un cambio de paleta no obligue a tocar la regla. */
const TONO_A_COLOR = { verde: 'success', azul: 'info', ambar: 'warning', gris: 'default' };

/**
 * La insignia, con la MISMA forma que en la ficha. Vive aquí porque el modal de detalles la
 * necesita, y la fila ya la trae: **el modal no pide nada al servidor, usa la fila del listado.**
 *
 * `null` es sin insignia y se devuelve nada, no un chip gris: a `general` la situación de un
 * compañero inactivo **no le viaja**, y pintar un hueco gris sería decir algo igualmente.
 */
function InsigniaSituacion({ insignia }) {
    if (!insignia) return null;
    return (
        <Chip
            size="small"
            label={insignia.texto}
            color={TONO_A_COLOR[insignia.tono] || 'default'}
            sx={{ height: 22, fontWeight: 600 }}
        />
    );
}

function PuntoSituacion({ insignia }) {
    if (!insignia) return <span style={{ display: 'inline-block', width: 10 }} aria-hidden="true" />;
    return (
        <span
            role="img"
            aria-label={insignia.texto}
            title={insignia.texto}
            style={{
                display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                backgroundColor: COLOR_PUNTO[insignia.tono] || COLOR_PUNTO.gris,
                flexShrink: 0,
            }}
        />
    );
}

/* ── LA BARRA EN DOS FILAS FIJAS (31/10) ─────────────────────────────────────────────────────
 *
 *   fila 1   Crear y Exportar a la izquierda; «Mostrar dependientes» a la derecha
 *   fila 2   los filtros, la búsqueda y la goma, en UNA línea desde `lg` (1200px, sin wrap)
 *
 * Los filtros ENCOGEN en vez de saltar: `1 1 auto` con un mínimo por filtro, sin ancho fijo. Por
 * debajo de `lg` la fila SÍ envuelve: era `md`, y entre 900 y 1200 los mínimos no cabían en una
 * línea —a 1366 al 125% faltaban 255px—, así que la goma se cortaba y la página hacía scroll
 * horizontal. Los mínimos se bajaron a la vez (31/10) para que 1280 al 100% quepa en una línea.
 *
 * La etiqueta sin encoger se recorta con «…» ANTES de la flecha: MUI le deja `100% - 24px`, y la
 * flecha de un Select o de un Autocomplete ocupa más que eso, así que la etiqueta larga se montaba
 * encima. 56px = el margen izquierdo de la etiqueta (14) + la flecha con su margen (~38) + aire. */
const ETIQUETA_SIN_PISAR_LA_FLECHA = {
    '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
        maxWidth: 'calc(100% - 56px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
    '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    '& .MuiAutocomplete-input': { textOverflow: 'ellipsis' },
};
const filtroElastico = (minimo) => ({
    ml: 0,
    mb: 0,
    width: { xs: 'calc(50% - 4px)', md: 'auto' },
    flex: { md: '1 1 auto' },
    minWidth: { md: minimo },
    ...ETIQUETA_SIN_PISAR_LA_FLECHA,
});
/* Un mínimo POR FILTRO, el que su etiqueta necesita para no recortarse: su ancho natural más los
 * 56px de la flecha, MEDIDO con size="small" a 1366 al 100% (31/10). Empresa se queda corta a
 * propósito (105; necesitaría 120) para que 1280 al 100% quepa en una línea. */
const FILTRO_NEGOCIO = filtroElastico(116);
const FILTRO_DEPARTAMENTO = filtroElastico(160);
const FILTRO_ROL = filtroElastico(80);
const FILTRO_ESTADO = filtroElastico(106);
const FILTRO_VERIFICACION = filtroElastico(142);
const FILTRO_EMPRESA = filtroElastico(105);
/* La búsqueda: `2 1 220px`, entre 120 y 360. Va en un grupo con la goma para que nunca se separen;
 * los límites del grupo son los de la búsqueda más la goma: 40px, lo que SE VE (ver su caja). */
const ANCHO_GOMA = 40;
/* La altura de la barra: filtros `small` (40px), botones y goma a 40. */
const ALTO_BARRA = 40;
const GRUPO_BUSQUEDA = {
    display: 'flex',
    alignItems: 'center',
    flex: { xs: '1 1 100%', md: `2 1 ${220 + ANCHO_GOMA}px` },
    minWidth: { md: 120 + ANCHO_GOMA },
    maxWidth: { md: 360 + ANCHO_GOMA },
};

function EnhancedTableToolbar(props) {
    const { numSelected, departamentos, roles, estados, empresas, empresasContratantes, esColaboradores, setEliminarSeleccion, isLoading, busqueda, data, selected, onCambiarEstadoActivo, meta } = props;

    // El id de «Alta», BUSCADO POR NOMBRE en la tabla `estados`. En las tres bases es el 3, pero
    // un identificador se mira, no se deduce — y aqui ya costo una vez (`personas.rol` cuando la
    // columna es `rol_id`). Si no estuviera, el filtro nace vacio y se ve todo: degradar hacia
    // «mas informacion» es lo seguro en un listado.
    const idEstadoAlta = String((estados || []).find(
        (e) => String(e?.nombre || '').trim().toLowerCase() === 'alta')?.Id ?? '');
    const [estadoBusqueda, setEstadoBusqueda] = useState(busqueda && busqueda.length > 0);
    const [escribiendo, setEscribiendo] = useState(false);
    const [negocioSeleccionado, setNegocioSeleccionado] = useState(null);
    const [departamentoSeleccionado, setDepartamentoSeleccionado] = useState(null);
    const [mostrarDependientes, setMostrarDependientes] = useState(false);
    const checkboxHabilitado = negocioSeleccionado && departamentoSeleccionado;
    
    const personasSeleccionadas = React.useMemo(() => {
        if (!data || !selected || selected.length === 0) return [];
        return data.filter(persona => selected.includes(persona.Id));
    }, [data, selected]);

    const estadoSwitch = React.useMemo(() => {
        if (personasSeleccionadas.length === 0) return { checked: false, indeterminate: false };
        
        const activas = personasSeleccionadas.filter(p => p.activo !== false);
        const inactivas = personasSeleccionadas.filter(p => p.activo === false);
        
        if (activas.length === personasSeleccionadas.length) {
            return { checked: true, indeterminate: false };
        } else if (inactivas.length === personasSeleccionadas.length) {
            return { checked: false, indeterminate: false };
        } else {
            return { checked: null, indeterminate: true }; // null para estado mixto
        }
    }, [personasSeleccionadas]);

    // `true` suspende, `false` levanta. Dos acciones, no un estado que adivinar.
    const handleSuspension = (suspenderlas) => {
        if (!onCambiarEstadoActivo || selected.length === 0) return;
        onCambiarEstadoActivo(selected, suspenderlas);
    };

    const location = useLocation();
    const navigate = useNavigate();
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const negocioURL = params.get('negocio');
        const departamentoURL = params.get('departamento');
        const mostrarDependientesURL = params.get('mostrar_dependientes') === 'true';

        setNegocioSeleccionado(negocioURL);

        // Buscar el objeto departamento por ID para el Autocomplete
        if (departamentoURL && departamentos) {
            const depObj = departamentos.find(d => String(d.Id) === departamentoURL);
            setDepartamentoSeleccionado(depObj || null);
        } else {
            setDepartamentoSeleccionado(null);
        }

        if (negocioURL && departamentoURL) {
            setMostrarDependientes(mostrarDependientesURL);
        } else {
            setMostrarDependientes(false);
        }
    }, [location.search, departamentos]);

    const toggleSeleccion = (valor, atributo) => {
        if (atributo === "ocultar") {
            setEliminarSeleccion((prevState) => ({
                ...prevState,
                valor,
            }));
        }
    };

    const handleMostrarDependientes = (event) => {
        const checked = event.target.checked;
        setMostrarDependientes(checked);
        
        const url = new URL(window.location);
        const params = new URLSearchParams(url.search);
        
        if (checked) {
            params.set('mostrar_dependientes', 'true');
        } else {
            params.delete('mostrar_dependientes');
        }
        
        navigate(`${url.pathname}?${params.toString()}`);
    };

    const departamentosFiltrados = React.useMemo(() => {
        if (!negocioSeleccionado || !departamentos) {
            return departamentos;
        }

        return departamentos.filter(dep => 
            dep.Negocios && dep.Negocios.some(negocio => 
                negocio.Id === parseInt(negocioSeleccionado)
            )
        );
    }, [negocioSeleccionado, departamentos]);

    return (
        <>
            <Toolbar
                sx={{
                    pl: { xs: 1, sm: 2 },
                    pr: { xs: 1, sm: 2 },
                    // Sin selección: dos filas fijas, una debajo de otra. Con selección, como antes.
                    flexDirection: numSelected > 0 ? { xs: 'column', sm: 'row' } : 'column',
                    alignItems: numSelected > 0 ? { xs: 'stretch', sm: 'center' } : 'stretch',
                    gap: numSelected > 0 ? { xs: 1, sm: 0 } : 1,
                    py: numSelected > 0 ? undefined : 1.5,
                    flexWrap: { xs: 'nowrap', sm: 'nowrap' },
                    ...(numSelected > 0 && {
                        bgcolor: (theme) =>
                            alpha(theme.palette.common.white, theme.palette.action.activatedOpacity),
                    }),
                }}
            >
                {numSelected > 0 ? (
                    <Typography
                        sx={{ 
                            flex: { xs: '1 1 auto', sm: '1 1 100%' },
                            mb: { xs: 1, sm: 0 }
                        }}
                        color="inherit"
                        variant="subtitle1"
                        component="div"
                    >
                        {numSelected} selecionado(s)
                    </Typography>
                ) : (
                    // Fase B · las ACCIONES SOBRE EL CONJUNTO van juntas. Crear sigue protegido;
                    // exportar NO va dentro de ese ProtectedView, y es deliberado: los básicos son
                    // un directorio de contactos que ya se ve en pantalla, y quien alcanza este
                    // listado incluye el rol `general` — 264 de 269 personas en TEST. Lo que
                    // cambia respecto a mirar es el volumen y la permanencia, y eso lo cubre la
                    // auditoría, que hoy con el listado no existe.
                    // Qué campos puede elegir cada rol lo resuelve el SERVIDOR; aquí no se decide.
                    // FILA 1: las acciones a la izquierda, «Mostrar dependientes» a la derecha.
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        {/* Crear y Exportar a la altura de los filtros. Sin margen vertical: el de 10
                            de `BotonesAccion` sumaba 20px a la fila. */}
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', '& .MuiButton-root': { height: ALTO_BARRA, my: 0 } }}>
                            <ProtectedView allowedRoles={["administrador", "super administrador", "recursos humanos"]}>
                                <BotonesAccion
                                    icono= 'addPersona'
                                    texto={esColaboradores ? "Crear Colaborador" : "Crear Empleado"}
                                    to={esColaboradores ? "/gestionpersonas/personas-colaboradoras/crearColaborador" : "/gestionpersonas/personas/crearPersona"}
                                />
                            </ProtectedView>
                            <BotonExportarExcel
                              estados={estados}
                                esColaboradores={esColaboradores}
                                total={meta?.total || 0}
                            />
                        </Box>
                        <FormControlLabel
                            sx={{
                                m: 0,
                                height: ALTO_BARRA,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: { xs: '1rem', sm: '0.875rem' }
                                }
                            }}
                            control={
                                <Checkbox
                                    checked={mostrarDependientes}
                                    onChange={handleMostrarDependientes}
                                    disabled={!checkboxHabilitado}
                                    size="small"
                                />
                            }
                            label="Mostrar dependientes"
                        />
                    </Box>
                )}

                {numSelected > 0 ? (
                    <>
                        {/* ── DOS BOTONES, NO UN INTERRUPTOR ────────────────────────────────
                            Era un `Switch` que escribía `activo` a pelo sobre la selección: el
                            camino más corto a una ficha incoherente, y en lote.

                            Ahora son DOS ACCIONES distintas —suspender y levantar—, y por eso son
                            dos botones. Un interruptor obliga a deducir un estado común de veinte
                            personas que pueden estar en cinco situaciones distintas; de ahí venía
                            el «Estado mixto», que no decía qué iba a pasar al pulsarlo.

                            Sólo se suspende desde Alta y sólo se levanta a quien está suspendido:
                            a quien no aplique, el resultado lo dirá con su nombre y su motivo. */}
                        <ProtectedView allowedRoles={["administrador", "super administrador"]}>
                            <div style={{ display: 'flex', gap: 8, marginRight: 20 }}>
                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleSuspension(true)}
                                    title="Quita el acceso sin dar de baja. Sólo a quien está en Alta."
                                >
                                    Suspender
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-success"
                                    onClick={() => handleSuspension(false)}
                                    title="Devuelve el acceso. Sólo a quien está suspendido."
                                >
                                    Levantar
                                </Button>
                            </div>
                        </ProtectedView>
                        <ProtectedView allowedRoles={["super administrador"]}>
                            {[
                                { mensaje: "Eliminar select", id: "eliminarSelect", handler: () => toggleSeleccion(false, "ocultar") },
                            ].map(({ mensaje, id, handler }) => (
                                <CustomTooltip key={id} mensaje={mensaje}>
                                    <div
                                        onClick={handler}
                                        style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        width: '40px', 
                                        height: '40px',
                                        marginRight: '25px',
                                        marginTop: '15px',
                                        }}
                                    >
                                        <IconosGenerales papeleraT="true" id={id} style={{width: '40px', height: '40px'}}/>
                                    </div>
                                </CustomTooltip>
                            ))}
                        </ProtectedView>
                    </>

                ) : (
                    // FILA 2: filtros, búsqueda y goma, en UNA línea desde `lg`. Ver la cabecera de
                    // `ETIQUETA_SIN_PISAR_LA_FLECHA`: los filtros encogen en vez de saltar.
                    //
                    // La goma va pegada a la búsqueda (nunca se separan) y lo que cede es el ancho
                    // de los filtros, no la fila. Por debajo de `lg` sí envuelve: ahí los mínimos no
                    // caben en una línea, y sin wrap la goma se cortaba y la página hacía scroll.
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: { xs: 'wrap', lg: 'nowrap' },
                            gap: '8px',
                            alignItems: 'center',
                        }}
                    >
                        {!isLoading ? (
                            <>
                                <SelectDinamicoLista size="small" sx={FILTRO_NEGOCIO}
                                    opciones={empresas.sort((a, b) => a.nombre.localeCompare(b.nombre))}
                                    parametroURL="negocio"
                                    etiqueta={esColaboradores ? "Empresa" : "Negocio"}//"Negocio"
                                    onChange={!esColaboradores ? setNegocioSeleccionado : undefined}
                                />
                                {!esColaboradores && (
                                <Autocomplete
                                    size="small"
                                    sx={FILTRO_DEPARTAMENTO}
                                    options={
                                        (departamentosFiltrados || departamentos || [])
                                            .sort((a, b) => a.nombre.localeCompare(b.nombre))
                                    }
                                    getOptionLabel={(option) => option.nombre || ''}
                                    value={departamentoSeleccionado}
                                    onChange={(e, newValue) => {
                                        setDepartamentoSeleccionado(newValue);
                                        const url = new URL(window.location);
                                        const params = new URLSearchParams(url.search);
                                        if (newValue) {
                                            params.set('departamento', newValue.Id);
                                        } else {
                                            params.delete('departamento');
                                            params.delete('mostrar_dependientes');
                                        }
                                        navigate(`${url.pathname}?${params.toString()}`);
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Departamento" />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                                    disabled={!departamentos || departamentos.length === 0}
                                />
                                )}
                                <ProtectedView allowedRoles={["administrador", "super administrador"]}>
                                  <SelectDinamicoLista size="small" sx={FILTRO_ROL} opciones={roles.sort((a, b) => a.nombre.localeCompare(b.nombre))} parametroURL="rol" etiqueta="Rol" />
                                </ProtectedView>
                                <ProtectedView allowedRoles={["administrador", "super administrador", "recursos humanos"]}>
                                  <SelectDinamicoLista size="small" sx={FILTRO_ESTADO} opciones={estados.sort((a, b) => a.nombre.localeCompare(b.nombre))} parametroURL="estado" etiqueta="Estado" valorPorDefecto={idEstadoAlta} valorTodos="todos" />
                                </ProtectedView>
                            </>

                        ) : (
                            <>
                                <SelectDinamicoLista size="small" sx={FILTRO_NEGOCIO} parametroURL="negocio" etiqueta={esColaboradores ? "Empresa" : "Negocio"}/>
                                {!esColaboradores && (
                                <Autocomplete
                                    size="small"
                                    sx={FILTRO_DEPARTAMENTO}
                                    options={[]}
                                    value={null}
                                    disabled
                                    renderInput={(params) => (
                                        <TextField {...params} label="Departamento" />
                                    )}
                                />
                                )}
                                <ProtectedView allowedRoles={["administrador", "super administrador"]}>
                                  <SelectDinamicoLista size="small" sx={FILTRO_ROL} parametroURL="rol" etiqueta="Rol" />
                                </ProtectedView>
                                <ProtectedView allowedRoles={["administrador", "super administrador", "recursos humanos"]}>
                                  <SelectDinamicoLista size="small" sx={FILTRO_ESTADO} parametroURL="estado" etiqueta="Estado" valorPorDefecto={idEstadoAlta} valorTodos="todos" />
                                </ProtectedView>
                            </>
                        )}
                        {/* Fase A1 · la verificación en dos pasos es UN FILTRO MÁS de esta barra, no
                            una fila aparte: se combina con negocio, rol, estado y búsqueda como
                            cualquier otro, porque va por la URL igual que ellos. */}
                        <ProtectedView allowedRoles={["administrador", "super administrador"]}>
                            <SelectDinamicoLista size="small" sx={FILTRO_VERIFICACION}
                                opciones={[
                                    { Id: 'con', nombre: 'Con verificación' },
                                    { Id: 'sin', nombre: 'Sin verificación' }
                                ]}
                                parametroURL="verificacion"
                                etiqueta="Verificación"
                            />
                        </ProtectedView>

                        {/* ── Empresa ──────────────────────────────────────────────────────
                            «Empresa», no «Empresa contratante»: al lado hay un filtro llamado
                            «Negocio», y con los dos nombres a la vista no hay ambigüedad. El
                            parámetro de la URL sigue siendo `empresacontratante` — `empresa` ya
                            está cogido en este componente y borra el departamento al cambiar.

                            RECURSOS HUMANOS entra aquí, y no de adorno: es quien rellena las
                            fichas que faltan. Son los mismos tres roles que pueden elegir el grupo
                            `laboral` de la exportación, y el servidor no manda la columna a los
                            demás: esconder el filtro no sería prohibirlo.

                            No se ofrece en colaboradoras: esa tabla no tiene la columna. */}
                        {!esColaboradores && (
                        <ProtectedView allowedRoles={["administrador", "super administrador", "recursos humanos"]}>
                            <SelectDinamicoLista size="small" sx={FILTRO_EMPRESA}
                                opciones={[
                                    // «Sin asignar» PRIMERA, y no por orden alfabético: es la que
                                    // usa RRHH para saber cuáles faltan, que es el trabajo que hay
                                    // encima de la mesa. Las razones sociales van debajo, ordenadas.
                                    //
                                    // Se retiró «Asignada»: «tiene alguna» no responde ninguna
                                    // pregunta que alguien se haga. Quien quiere saber de una razón
                                    // social quiere ESA, no «cualquiera».
                                    //
                                    // LA REGLA DEL AGREGADO, para cuando haga falta: si algún día
                                    // vuelve un recuento junto a este filtro, NO puede aplicarse su
                                    // propio filtro — filtrando «sin» daría el número de
                                    // resultados, y filtrando por una empresa daría siempre 0. Hoy
                                    // no hay recuento —se retiró a propósito— así que no hay nada
                                    // que obedezca la regla; queda escrita para que el día que
                                    // vuelva nazca obedeciéndola.
                                    { Id: 'sin', nombre: 'Sin asignar' },
                                    ...(empresasContratantes || []).map((e) => ({
                                        Id: e.Id, nombre: e.razon_social,
                                    })),
                                ]}
                                parametroURL="empresacontratante"
                                etiqueta="Empresa"
                            />
                        </ProtectedView>
                        )}

                        {/* ── LA BÚSQUEDA Y LA GOMA, EN UN SOLO GRUPO (31/10) ──────────────────
                            La goma iba suelta al final, detrás de «Mostrar dependientes»: cuando la
                            fila no cabía, era lo primero en saltar y quedaba sola en otra línea.
                            Ahora viajan juntas; los límites están en `GRUPO_BUSQUEDA`. */}
                        <Box sx={GRUPO_BUSQUEDA}>
                            <Buscador
                                placeholder="Busqueda avanzada"
                                tooltip="Ej: Nombre, Apellidos, Mail..."
                                palabra={busqueda || ""}
                                estadoBusqueda={estadoBusqueda}
                                setEstadoBusqueda={setEstadoBusqueda}
                                escribiendo={escribiendo}
                                setEscribiendo={setEscribiendo}
                                size="small"
                                sx={{ width: '100%', minWidth: 0, flex: '1 1 auto', ml: 0, mb: 0 }}
                            />
                            {/* `BotonesAccion` dibuja la goma a escala 1.4, y traía `min-width`, un
                                margen de 10 y los márgenes de un icono con texto: medido, se salía de
                                su caja. Aquí el botón mide 40/1.4 ≈ 28,6px, sin márgenes ni relleno,
                                y la escala lo deja en los 40 que SE VEN, centrado en una caja de 40. */}
                            <Box sx={{
                                flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: ANCHO_GOMA, height: ALTO_BARRA,
                                '& .MuiButton-root': { minWidth: 0, width: ANCHO_GOMA / 1.4, height: ALTO_BARRA / 1.4, p: 0, ml: 0, mr: 0 },
                                '& .MuiButton-startIcon': { m: 0 },
                            }}>
                                <BotonesAccion
                                    icono="delete"
                                    to="/gestionpersonas/personas"
                                />
                            </Box>
                        </Box>
                    </Box>
                )}
            </Toolbar >
        </>
    );
}

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};

function EnhancedTable(props) {

    const navigate = useNavigate();
    const { persona } = useContext(UsuarioContext);
    const esAdmin = ['administrador', 'super administrador'].includes(persona?.rol?.toLowerCase());
    // Provincia y población son datos personales: el servidor sólo los manda a los roles de la
    // ficha (hotfix 23/10). Al resto no se le pinta el bloque: «No asignado» sería falso.
    const veUbicacion = puedeAbrirFichas(persona?.rol);
    const {
        data,
        header,
        meta,
        roles,
        estados,
        empresas,
        departamentos,
        selected,
        setSelected,
        eliminarSeleccion,
        setEliminarSeleccion,
        isLoading,
        busqueda,
        empresasContratantes,
        onCambiarEstadoActivo,
        esColaboradores,
        encimaDeLaTabla = null,
        textoVacio = 'No hay personas con estos filtros.',
        totalGrupos = 0
    } = props;

    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('nombre');
    const [abrirModal, setAbrirModal] = useState(false);
    const [personaSeleccionada, setPersonaSeleccionada] = useState(null);
    const [copiedPasswords, setCopiedPasswords] = React.useState({});

    const esCumpleanos = (fechaNacimiento) => {
        if (!fechaNacimiento) return false;
        
        const hoy = new Date();
        const nacimiento = new Date(fechaNacimiento);
        
        return (
            hoy.getDate() === nacimiento.getDate() &&
            hoy.getMonth() === nacimiento.getMonth()
        );
    };

    const handleRequestSort = (event, property) => {
        const nuevaURL = funcionOrdenarColumnas(orderBy, order, setOrderBy, setOrder)(event, property);
        navigate(nuevaURL);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = data.map((n) => n.Id);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    const handleRowClick = (event, row) => {
        if (event.target.type === 'checkbox' || 
            event.target.closest('button') || 
            event.target.closest('a')) {
            return;
        }
        setPersonaSeleccionada(row);
        setAbrirModal(true);
    };

    const handleCloseModal = () => {
        setAbrirModal(false);
    };

    const obtenerTextoDireccionEnvio = (persona) => {
        if (!persona) return 'No definida';
        const direccionEnvio = persona.direccion_envio;

        if (direccionEnvio === 'personal' || !direccionEnvio) {
            if (!persona.direccion) return 'Dirección personal (No definida)';
            return `Dirección personal (${[persona.direccion, persona.codigo_postal, persona.poblacion].filter(Boolean).join(', ')})`;
        } else if (direccionEnvio?.startsWith('oficina-')) {
            const oficinaId = parseInt(direccionEnvio.split('-')[1]);
            const oficina = persona.Oficinas?.find(o => o.Id === oficinaId);
            if (!oficina) return 'Oficina no encontrada';
            const direccionOficina = [oficina.direccion, oficina.cp, oficina.poblacion].filter(Boolean).join(', ');
            return direccionOficina ? `${oficina.nombre} (${direccionOficina})` : oficina.nombre;
        } else {
            return `Personalizada (${direccionEnvio})`;
        }
    };

    const isSelected = (id) => selected.indexOf(id) !== -1;

    const sortedData = stableSort(data);

    const handleCopiarPass = (rowId, field, value) => {
        if (navigator.clipboard && value) {
            navigator.clipboard.writeText(value);
            setCopiedPasswords((prev) => ({ ...prev, [`${rowId}_${field}`]: true }));
            setTimeout(() => {
                setCopiedPasswords((prev) => ({ ...prev, [`${rowId}_${field}`]: false }));
            }, 1000);
        }
    };

    const renderCellContent = (row, headerId) => {
        switch (headerId) {
            case 'nombre':
                return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <PuntoSituacion insignia={row.insignia} />
                        {row.nombre}
                    </span>
                );
            case 'apellidos':
                return row.apellidos;
            case 'telefono_empresa':
                if(!row.telefono_empresa) 
                  return '';
                // let telefonoEditado = row.telefono_empresa.slice(0, 3) + ' ' + row.telefono_empresa.slice(3, 6) + ' ' + row.telefono_empresa.slice(6);
                // return telefonoEditado;
                const telefonoEditado = row.telefono_empresa.match(/.{1,3}/g)?.join(' ');
                return telefonoEditado;
            case 'extension_telefono':
                return row.extension_telefono;
             case 'telefono_fijo': {
                const telefonoFijo = row.telefono_fijo;
                if (!telefonoFijo) return '';
                return `${telefonoFijo.slice(0, 3)} ${telefonoFijo.slice(3, 6)} ${telefonoFijo.slice(6)}`;
            }
            case 'mail_empresa':
                // Internas: el principal y, si la búsqueda coincidió con un secundario, ese debajo y
                // resaltado (30/10). Las colaboradoras no tienen secundarios.
                return esColaboradores ? row.mail_empresa : <CeldaCorreo fila={row} busqueda={busqueda} />;
            case 'usuario_nas':
                return row.usuario_nas;
            case 'password_nas':
            case 'password_m365': {
                const value = row[headerId];
                const copied = copiedPasswords[`${row.Id}_${headerId}`];
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {value && (
                            <Tooltip title={copied ? '¡Copiado!' : 'Copiar al portapapeles'} open={copied ? true : undefined} disableFocusListener disableTouchListener>
                                <span
                                    style={{ cursor: 'pointer', display: 'flex' }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleCopiarPass(row.Id, headerId, value);
                                    }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </span>
                            </Tooltip>
                        )}
                    </div>
                );
            }
            case 'provincia':
                return row.provincia;
            case 'poblacion':
                return row.poblacion;
            case 'oficinas':
                if (!row.Oficinas || row.Oficinas.length === 0) {
                    return 'Sin oficinas';
                }
                return (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Chip 
                            label={row.Oficinas[0].nombre} 
                            size="small" 
                            style={{ backgroundColor: '#336385', color: 'white' }}
                        />
                        {row.Oficinas.length > 1 && (
                            <Chip 
                                label={`+${row.Oficinas.length - 1}`} 
                                size="small" 
                                style={{ backgroundColor: '#e0e0e0' }}
                            />
                        )}
                    </div>
                );
            case 'rol':
                return row.Rol?.nombre;
            case 'fecha_alta':
                return new Date(row.fecha_alta).toLocaleDateString('es-ES');
            case 'conexion_remota':
                return (
                    <Col xs="auto" className="d-flex align-items-center">
                        <Button
                            style={{
                                height: '30px',
                                width: '30px',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none',
                                outline: 'none',
                                boxShadow: 'none',
                                backgroundColor: 'transparent',
                            }}
                            onClick={() => {
                                if (row.conexion_remota) {
                                    window.location.href = `anydesk://${row.conexion_remota.replace(/\s+/g, '')}`;
                                }
                            }}
                            hidden={!row.conexion_remota}
                        >
                            <img
                                src={LogoAnyDesk}
                                alt="AnyDesk"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                        </Button>
                    </Col>
                );
            case 'modificar':
                return (
                    <IconosGenerales
                        tabla="true"
                        id="edit"
                        to={esColaboradores ? `/gestionpersonas/personas-colaboradoras/editarColaborador/${row.Id}${window.location.search}` : `/gestionpersonas/personas/editarPersona/${row.Id}${window.location.search}`}
                    />
                );
            default:
                return row[headerId] || '';
        }
    };

    return (
      <Box sx={{ width: "100%" }}>
        <div>
          <Paper sx={{ width: "98%", mb: 2 }}>
            <EnhancedTableToolbar
              numSelected={selected.length}
              departamentos={departamentos}
              roles={roles}
              estados={estados}
              empresas={empresas}
              esColaboradores={esColaboradores}
              eliminarSeleccion={eliminarSeleccion}
              setEliminarSeleccion={setEliminarSeleccion}
              isLoading={isLoading}
              busqueda={busqueda}
              data={data}
              selected={selected}
              onCambiarEstadoActivo={onCambiarEstadoActivo}
              meta={meta}
              empresasContratantes={empresasContratantes}
            />
            {/* Lo que va entre el buscador y las filas: los buzones y listas que coinciden (30/10). */}
            {encimaDeLaTabla}
            {/* AL 100% DEL CONTENEDOR (31/10). Llevaba `maxWidth: 2000`: con la ventana ancha o el
                zoom alejado, el contenedor pasaba de 2000px y la tabla se quedaba corta —516px a
                1920 al 67%—, descuadrada con la cabecera azul. `minWidth` se queda: es el ancho por
                debajo del cual las columnas no caben, y entonces el contenedor hace scroll
                horizontal en vez de aplastarlas. */}
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table
                sx={{ width: '100%', minWidth: 1300 }}
                aria-labelledby="tableTitle"
              >
                <EnhancedTableHead
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={data.length}
                  header={header}
                />
                <TableBody>
                  {isLoading ? (
                    <StyledTableRow hover sx={{ cursor: "default" }}>
                      <StyledTableCell
                        colSpan={13}
                        style={{ textAlign: "center" }}
                      >
                        <CircularProgress
                          style={{ color: "rgba(0, 60, 102, 0.8)" }}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : sortedData.length === 0 ? (
                    // Sin esta fila, una búsqueda sin resultados dejaba la tabla en blanco, sin decir nada.
                    <StyledTableRow sx={{ cursor: "default" }}>
                      <StyledTableCell colSpan={header.length + 1} style={{ textAlign: "center", padding: 32, color: '#64748b' }}>
                        {textoVacio}
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : (
                    sortedData.map((row, index) => {
                      const isItemSelected = isSelected(row.Id);
                      const labelId = `enhanced-table-checkbox-${index}`;
                      const fondoFila = fondoDe(row);
                      return (
                        <StyledTableRow
                          hover
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row.Id}
                          selected={isItemSelected}
                          onClick={(event) => handleRowClick(event, row)}
                          sx={{ cursor: "pointer" }}
                          fondo={fondoFila}
                        >
                          <StyledTableCell padding="checkbox">
                            <ProtectedView
                              allowedRoles={[
                                "administrador",
                                "super administrador",
                              ]}
                            >
                              <Checkbox
                                color="default"
                                onClick={(event) => {
                                  handleClick(event, row.Id);
                                }}
                                checked={isItemSelected}
                                inputProps={{ "aria-labelledby": labelId }}
                              />
                            </ProtectedView>
                          </StyledTableCell>
                          {header.map((headCell, cellIndex) => {
                            const isFirstCell = cellIndex === 0;
                            const isActionCell = headCell.id === "modificar";

                            return (
                              <ProtectedView
                                key={headCell.id}
                                allowedRoles={
                                  headCell.allowedRoles || [
                                    "administrador",
                                    "super administrador",
                                    "gestor de contenidos",
                                    "general",
                                    "recursos humanos",
                                  ]
                                }
                              >
                                <StyledTableCell
                                  component={isFirstCell ? "th" : undefined}
                                  id={isFirstCell ? labelId : undefined}
                                  scope={isFirstCell ? "row" : undefined}
                                  padding={isFirstCell ? "none" : "normal"}
                                  align={isActionCell ? "right" : "left"}
                                  sx={headCell.id === 'telefono_empresa' ? { minWidth: 130, whiteSpace: 'nowrap' } : undefined}
                                >
                                  {renderCellContent(row, headCell.id)}
                                </StyledTableCell>
                              </ProtectedView>
                            );
                          })}
                        </StyledTableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </div>
        <PaginacionTabla totalPages={meta.totalPages} />
        <SelectNumeroCursos perPage={meta.per_page || 10} />

        <Typography
          variant="body2"
          sx={{
            mr: "2%",
            textAlign: "right",
            fontWeight: "bold",
            fontSize: "16px",
            gap: "8px",
            marginTop: "-25px",
            color: "rgba(0, 60, 102, 0.8)",
          }}
        >
          {/* Con buzones o listas en la banda de arriba, el pie cuenta las dos cosas (31/10). */}
          {totalGrupos > 0
            ? `${meta.total || 0} ${(meta.total || 0) === 1 ? 'persona' : 'personas'} · ${totalGrupos} ${totalGrupos === 1 ? 'buzón/lista' : 'buzones/listas'}`
            : `Total registros: ${meta.total || 0}`}
        </Typography>

        <Dialog
          open={abrirModal}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
        >
          {personaSeleccionada && (
            <>
              <DialogTitle
                sx={{ m: 0, p: 2, bgcolor: "#336384", color: "white" }}
              >
                Detalles de {personaSeleccionada.nombre}{" "}
                {personaSeleccionada.apellidos}
                {/* Es el sitio donde MÁS sentido tiene: lo que alguien abre para saber algo
                    rápido de un compañero. Y no estaba — la insignia se puso donde se diseñó,
                    no donde la gente la busca.
                    La compone el servidor y la fila ya la trae; aquí sólo se pinta. */}
                {personaSeleccionada.insignia && (
                    <Box component="span" sx={{ ml: 1.5, verticalAlign: 'middle' }}>
                        <InsigniaSituacion insignia={personaSeleccionada.insignia} />
                    </Box>
                )}
                <IconButton
                  aria-label="close"
                  onClick={handleCloseModal}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "white",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={1}>
                  {personaSeleccionada.foto ? (
                    <>
                      {/* Columna para la imagen (solo se muestra si hay foto) */}
                      <Grid
                        item
                        xs={12}
                        md={3}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                          pt: 2,
                        }}
                      >
                        <div
                          style={{
                            position: "relative",
                            width: "170px",
                            height: "170px",
                          }}
                        >
                          <img
                            src={`${process.env.REACT_APP_API_URL}/api/persona/imagen/${personaSeleccionada.foto}?formato=grande`}
                            alt="Foto de perfil"
                            style={{
                              width: "170px",
                              height: "170px",
                              //   borderRadius: '50%',
                              border: "2px solid rgba(0, 60, 102, 0.8)",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.src = ImagenDefault;
                            }}
                          />
                          {esCumpleanos(
                            personaSeleccionada.fecha_nacimiento
                          ) && (
                            <img
                              src={GorraCumpleanos}
                              alt="Cumpleaños"
                              style={{
                                position: "absolute",
                                top: "-25px",
                                right: "-25px",
                                width: "60px",
                                height: "60px",
                                zIndex: 1,
                                transform: "rotate(25deg)",
                                pointerEvents: "none",
                              }}
                            />
                          )}
                        </div>
                      </Grid>

                      {/* Columna con la información (ocupa 9/12 cuando hay foto) */}
                      <Grid item xs={12} md={9}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Información personal
                            </Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Typography>
                              <strong>Nombre:</strong>{" "}
                              {personaSeleccionada.nombre}
                            </Typography>
                            <Typography>
                              <strong>Apellidos:</strong>{" "}
                              {personaSeleccionada.apellidos}
                            </Typography>
                          </Grid>
                          {veUbicacion && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Ubicación
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Typography>
                              <strong>Provincia:</strong>{" "}
                              {personaSeleccionada.provincia || "No asignado"}
                            </Typography>
                            <Typography>
                              <strong>Población:</strong>{" "}
                              {personaSeleccionada.poblacion || "No asignado"}
                            </Typography>
                            {esAdmin && (
                              <Typography>
                                <strong>Dirección de Envío:</strong>{" "}
                                {obtenerTextoDireccionEnvio(personaSeleccionada)}
                              </Typography>
                            )}
                          </Grid>
                          )}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Información de contacto
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Typography>
                              <strong>Teléfono:</strong>{" "}
                              {personaSeleccionada.telefono_empresa ||
                                "No asignado"}
                            </Typography>
                            {personaSeleccionada.extension_telefono && (
                              <Typography>
                                <strong>Extensión:</strong>{" "}
                                {personaSeleccionada.extension_telefono ||
                                  "No asignado"}
                              </Typography>
                            )}
                              <Typography>
                                <strong>Teléfono fijo:</strong>{" "}
                                {personaSeleccionada.telefono_fijo ||
                                  "No asignado"}
                              </Typography>
                            <Typography>
                              <strong>Email:</strong>{" "}
                              <a
                                href={`mailto:${personaSeleccionada.mail_empresa}`}
                                style={{
                                  color: "rgb(51,99,132)",
                                  textDecoration: "underline",
                                }}
                              >
                                {personaSeleccionada.mail_empresa}
                              </a>
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Oficinas
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {personaSeleccionada.Oficinas && personaSeleccionada.Oficinas.length > 0 ? (
                              <Box component="ul" sx={{ margin: 0, paddingLeft: '20px', listStyleType: 'disc' }}>
                                {personaSeleccionada.Oficinas.map((oficina, index) => (
                                  <Box component="li" key={index} sx={{ display: 'list-item' }}>
                                    <Typography component="span">{oficina.nombre}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography>No asignadas</Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Información personal
                            </Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Typography>
                              <strong>Nombre:</strong>{" "}
                              {personaSeleccionada.nombre}
                            </Typography>
                            <Typography>
                              <strong>Apellidos:</strong>{" "}
                              {personaSeleccionada.apellidos}
                            </Typography>
                          </Grid>
                          {veUbicacion && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Ubicación
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Typography>
                              <strong>Provincia:</strong>{" "}
                              {personaSeleccionada.provincia || "No asignado"}
                            </Typography>
                            <Typography>
                              <strong>Población:</strong>{" "}
                              {personaSeleccionada.poblacion || "No asignado"}
                            </Typography>
                            {esAdmin && (
                              <Typography>
                                <strong>Dirección de Envío:</strong>{" "}
                                {obtenerTextoDireccionEnvio(personaSeleccionada)}
                              </Typography>
                            )}
                          </Grid>
                          )}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Información de contacto
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Typography>
                              <strong>Teléfono:</strong>{" "}
                              {personaSeleccionada.telefono_empresa ||
                                "No asignado"}
                            </Typography>
                            {personaSeleccionada.extension_telefono && (
                              <Typography>
                                <strong>Extensión:</strong>{" "}
                                {personaSeleccionada.extension_telefono ||
                                  "No asignado"}
                              </Typography>
                            )}
                              <Typography>
                                <strong>Teléfono fijo:</strong>{" "}
                                {personaSeleccionada.telefono_fijo ||
                                  "No asignado"}
                              </Typography>
                            <Typography>
                              <strong>Email:</strong>{" "}
                              <a
                                href={`mailto:${personaSeleccionada.mail_empresa}`}
                                style={{
                                  color: "rgb(51,99,132)",
                                  textDecoration: "underline",
                                }}
                              >
                                {personaSeleccionada.mail_empresa}
                              </a>
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Oficinas
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {personaSeleccionada.Oficinas && personaSeleccionada.Oficinas.length > 0 ? (
                              <Box component="ul" sx={{ margin: 0, paddingLeft: '20px', listStyleType: 'disc' }}>
                                {personaSeleccionada.Oficinas.map((oficina, index) => (
                                  <Box component="li" key={index} sx={{ display: 'list-item' }}>
                                    <Typography component="span">{oficina.nombre}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography>No asignadas</Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                  {personaSeleccionada.Cargos &&
                    personaSeleccionada.Cargos.filter((cargo) => {
                      const departamento = departamentos.find(
                        (d) => d.Id === cargo.departamento_id
                      );
                      return departamento && departamento.tipo === "interno";
                    }).length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {personaSeleccionada.Cargos.filter((cargo) => {
                            const departamento = departamentos.find(
                              (d) => d.Id === cargo.departamento_id
                            );
                            return (
                              departamento && departamento.tipo === "interno"
                            );
                          }).length === 1
                            ? "Cargo"
                            : "Cargos"}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <TableContainer
                          component={Paper}
                          sx={{ maxHeight: 200, mb: 2 }}
                        >
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: "#e8f1f5",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 10
                                  }}
                                >
                                  Negocio
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: "#e8f1f5",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 10
                                  }}
                                >
                                  Departamento
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: "#e8f1f5",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 10
                                  }}
                                >
                                  Cargo
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {personaSeleccionada.Cargos.filter((cargo) => {
                                const departamento = departamentos.find(
                                  (d) => d.Id === cargo.departamento_id
                                );
                                return (
                                  departamento &&
                                  departamento.tipo === "interno"
                                );
                              }).map((cargo, index) => {
                                const empresa = empresas.find(
                                  (e) => e.Id === cargo.empresa_id
                                );
                                const departamento = departamentos.find(
                                  (d) => d.Id === cargo.departamento_id
                                );

                                return (
                                  <TableRow key={index} hover>
                                    <TableCell>
                                          {empresa
                                            ? empresa.nombre
                                            : `Empresa ID: ${cargo.empresa_id}`}
                                    </TableCell>
                                    <TableCell>
                                      {departamento
                                        ? departamento.nombre
                                        : `Departamento ID: ${cargo.departamento_id}`}
                                    </TableCell>
                                    <TableCell>{cargo.cargo}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  style={{
                    backgroundColor: "rgb(51,99,132)",
                    borderColor: "rgb(51,99,132)",
                  }}
                  onClick={handleCloseModal}
                >
                  Cerrar
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    );
}

EnhancedTable.propTypes = {
    data: PropTypes.array.isRequired,
    header: PropTypes.array.isRequired,
    meta: PropTypes.object.isRequired,
};

export default EnhancedTable;